// ------------------------------------------------------------------
// reportColor — a contrast-safe "secondary/muted text" tint derived from a
// template's accent color (story 4-82; P06 §4).
//
// `ReportRender.tsx`'s own header comment establishes the rule this file
// exists to honor without breaking: accent is tenant DATA (an org's brand
// color, chosen freely in the designer), so it can never be trusted as text
// color outright — an org could pick a pale yellow and make text
// unreadable. This derives a MUTED tint (same hue, fixed moderate
// saturation, walked-down lightness) that is provably WCAG-AA (>=4.5:1)
// against a GUARANTEED WHITE surface — which is why every caller of this
// function is scoped to a flat-white background (the PDF page, and
// `ReportRender`'s 'print' variant, which forces `bg-white`) and NEVER the
// 'screen' variant's flat `bg-card` surface, which is theme-dependent, not a
// fixed color this function could validate against.
//
// `backend/src/services/pdf/pdfExport.service.js` keeps a byte-for-byte
// twin of this function (same "backend twin, no shared module" pattern this
// whole render pipeline already uses — see that file's own header comment)
// so the derivation math cannot silently drift between the two surfaces it
// targets. `frontend/scripts/contrast-check.mjs` keeps a THIRD twin, purely
// to assert this algorithm clears 4.5:1 for representative accent hues — it
// cannot import this file (a standalone script, no build step), so it
// re-implements the same math and is the actual gate that fails a commit if
// any of the three twins drifts into an unsafe result.
// ------------------------------------------------------------------

const MIN_CONTRAST = 4.5;
const MUTED_SATURATION = 35;

function hexToRgb(hex: string): [number, number, number] | null {
  const cleaned = hex.replace('#', '').trim();
  const full = cleaned.length === 3 ? cleaned.split('').map((c) => c + c).join('') : cleaned;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function rgbToHsl([r, g, b]: [number, number, number]): number {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else h = ((rn - gn) / d + 4) / 6;
  }
  return h * 360;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const sn = s / 100;
  const ln = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const cn = c / 255;
    return cn <= 0.03928 ? cn / 12.92 : ((cn + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const hi = Math.max(relativeLuminance(a), relativeLuminance(b));
  const lo = Math.min(relativeLuminance(a), relativeLuminance(b));
  return (hi + 0.05) / (lo + 0.05);
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('')}`;
}

const WHITE: [number, number, number] = [255, 255, 255];

/**
 * Derive a muted/secondary text color from an accent hex, guaranteed
 * WCAG-AA (>=4.5:1) against a WHITE background. Returns `null` for an
 * absent/invalid accent — same "nothing to derive from" honesty every other
 * accent-dependent branch in this render pipeline already applies.
 */
export function deriveSecondaryTextColor(accentHex: string | null | undefined): string | null {
  if (!accentHex) return null;
  const rgb = hexToRgb(accentHex);
  if (!rgb) return null;
  const h = rgbToHsl(rgb);
  let l = 45;
  let candidate = hslToRgb(h, MUTED_SATURATION, l);
  while (contrastRatio(candidate, WHITE) < MIN_CONTRAST && l > 5) {
    l -= 2;
    candidate = hslToRgb(h, MUTED_SATURATION, l);
  }
  return rgbToHex(candidate);
}
